import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, ChevronLeft, Calendar, CreditCard, CheckCircle2, AlertCircle,
  Zap, XCircle, RefreshCw, Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const heading = { fontFamily: "'Satoshi', sans-serif" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return iso; }
};

export default function ManageSubscriptionPage() {
  const { isAuthenticated, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const fetchManage = async () => {
    try {
      const res = await axios.get(`${API}/subscription/manage`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load subscription");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) fetchManage();
  }, [isAuthenticated]);

  const cancelAutoRenew = async () => {
    if (!window.confirm("Cancel auto-renew? You'll keep access until your current cycle ends.")) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel-subscription`, {}, { withCredentials: true });
      toast.success("Auto-renew cancelled. Your plan stays active until expiry.");
      await fetchManage();
      if (refreshUser) refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancellation failed");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">No subscription data</div>;
  }

  const isPaid = ["growth", "yearly"].includes(data.plan);
  const isTrial = data.trial_active;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6" data-testid="manage-subscription-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/settings" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mb-2">
          <ChevronLeft className="w-3.5 h-3.5" /> Settings
        </Link>
        <h1 className="text-2xl sm:text-3xl font-black text-slate-900 mb-1" style={heading}>Manage Subscription</h1>
        <p className="text-sm text-slate-500 mb-6">View your current plan, next renewal date, and cancel auto-renew anytime.</p>

        {/* Current plan card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-5" data-testid="current-plan-card">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Current Plan</p>
              <h2 className="text-2xl font-black text-slate-900 capitalize" style={heading}>
                {data.plan === "free" ? "Free" : data.plan === "yearly" ? "Yearly" : "Growth"}
              </h2>
              {isTrial && (
                <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
                  <Sparkles className="w-3 h-3" /> Trial active
                </span>
              )}
            </div>
            {!isPaid && (
              <Link to="/pricing" data-testid="upgrade-cta-btn">
                <Button className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="upgrade-cta">
                  <Zap className="w-4 h-4 mr-1.5" /> Upgrade
                </Button>
              </Link>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Next billing date</p>
              <p className="text-sm text-slate-700 inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span data-testid="next-billing-date">{fmtDate(data.next_billing_date) || "—"}</span>
              </p>
            </div>
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Next billing amount</p>
              <p className="text-sm text-slate-700 inline-flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-slate-400" />
                <span data-testid="next-billing-amount">
                  {data.renewal_amount_inr ? `₹${data.renewal_amount_inr.toLocaleString("en-IN")}` : "—"}
                  {data.plan === "growth" && " / month"}
                  {data.plan === "yearly" && " / year"}
                </span>
              </p>
            </div>
            {data.intro_until_date && data.plan === "growth" && (
              <div className="sm:col-span-2 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800" data-testid="promo-notice">
                You're in the promotional pricing window. Auto-debit at ₹{data.renewal_amount_inr}/month starts on <span className="font-semibold">{fmtDate(data.intro_until_date)}</span>.
              </div>
            )}
            <div className="sm:col-span-2">
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Auto-renew</p>
              {data.auto_renew ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700" data-testid="auto-renew-on">
                  <CheckCircle2 className="w-4 h-4" /> Enabled — your plan will renew automatically
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm text-slate-500" data-testid="auto-renew-off">
                  <AlertCircle className="w-4 h-4 text-amber-500" /> Not enabled — plan ends at expiry unless renewed
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        {isPaid && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6" data-testid="manage-actions">
            <h3 className="font-bold text-slate-900 mb-3">Actions</h3>
            <div className="flex flex-wrap gap-2">
              {data.auto_renew ? (
                <Button
                  onClick={cancelAutoRenew}
                  disabled={cancelling}
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="cancel-autorenew-btn"
                >
                  {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <XCircle className="w-4 h-4 mr-1.5" />}
                  Cancel auto-renew
                </Button>
              ) : (
                <Link to="/pricing">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="reactivate-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Reactivate auto-renew
                  </Button>
                </Link>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Cancelling won't end your access immediately — you'll keep all features until {fmtDate(data.next_billing_date)}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
